import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import moneyCurrency2 from "../assets/money-currency2.png";
import check from "../assets/check.png";
import star from "../assets/star.png";
import calendar from "../assets/calendar.png";
import rupeeIcon from "../assets/rupees.png";
import { useSuperAdmin } from "../context/SuperAdminContext";


const PaymentDetailModal = ({ isOpen, onClose, workerId }) => {
  const navigate = useNavigate();
  const { fetchPaymentHistory, verifyToken } = useSuperAdmin();

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [payments, setPayments] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [accountDetails, setAccountDetails] = useState({ accountNumber: "" });
  const [summary, setSummary] = useState({
    totalAmount: 0,
    totalNetAmount: 0,
    attendancePayout: 0,
    incentivePayout: 0,
    shiftCount: 0,
  });

  const getCurrentMonthYear = () => {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}`;
  };
  const [selectedPeriod, setSelectedPeriod] = useState(getCurrentMonthYear());

  const generateMonthYearOptions = () => {
    const options = [{ value: "all", label: "All Periods" }];
    const startDate = new Date(2025, 0);
    const currentDate = new Date();
    for (
      let date = new Date(startDate);
      date <= currentDate;
      date.setMonth(date.getMonth() + 1)
    ) {
      options.push({
        value: `${date.getFullYear()}-${date.getMonth() + 1}`,
        label: `${date.toLocaleString("default", { month: "short" })}, ${date.getFullYear()}`,
      });
    }
    return options;
  };

  const formatAccountNumber = (accountNo) => {
    if (!accountNo) return "-";
    const first = accountNo.charAt(0);
    const last4 = accountNo.slice(-4);
    return `${first}${"X".repeat(Math.max(accountNo.length - 5, 0))}${last4}`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    const dateObj = new Date(isoString);
    const day = dateObj.getUTCDate().toString().padStart(2, "0");
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const month = monthNames[dateObj.getUTCMonth()];
    const year = String(dateObj.getUTCFullYear()).slice(-2);
    let hours = dateObj.getUTCHours();
    const minutes = dateObj.getUTCMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day} ${month}'${year} | ${hours}:${minutes} ${ampm}`;
  };

  const formatPaidOn = (isoString) => {
    if (!isoString) return null;
    const dateObj = new Date(isoString);
    const day = dateObj.getUTCDate();
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const month = monthNames[dateObj.getUTCMonth()];
    const year = String(dateObj.getUTCFullYear()).slice(-2);
    return `${day} ${month}'${year}`;
  };

  const getStatus = (status) => {
    const s = status?.toLowerCase();
    if (s === "success" || s === "successful") return { label: "Successful", color: "#16a34a" };
    if (s === "pending") return { label: "Pending", color: "#d97706" };
    if (s === "failed") return { label: "Failed", color: "#dc2626" };
    if (s === "payoutgenerated") return { label: "Payout Generated", color: "#2563eb" };
    return { label: status, color: "#6b7280" };
  };

  // period -> resets to page 1 and REPLACES the list
  // append=true -> used for "Next Page" and APPENDS results, keeping loadingMore separate from loading
  const loadData = useCallback(
    async (period, pageNum = 1, append = false) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        let params = { page: pageNum };
        if (period !== "all") {
          const [year, month] = period.split("-");
          const startDate = new Date(year, month - 1, 1).toLocaleDateString("en-CA");
          const endDate = new Date(year, month, 0).toLocaleDateString("en-CA");
          params = { ...params, startDate, endDate };
        }

        const [historyRes, accountRes] = await Promise.all([
          fetchPaymentHistory(params, workerId),
        ]);

        const data = historyRes?.data?.data ?? historyRes?.data ?? historyRes;
        if (data?.summary) setSummary(data.summary);
        if (data?.payments) {
          setPayments((prev) => (append ? [...prev, ...data.payments] : data.payments));
        }
        if (data?.pagination) setHasNextPage(data.pagination.hasNextPage);

        const acc = accountRes?.data ?? accountRes;
        if (acc?.accountNumber) setAccountDetails(acc);
      } catch (error) {
        console.error("Error loading payment detail modal:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [fetchPaymentHistory, workerId]
  );

  // modal open / period change -> reset to page 1 and replace list
  useEffect(() => {
    if (isOpen) {
      setPage(1);
      loadData(selectedPeriod, 1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedPeriod]);

  const handleNextPage = () => {
    if (loadingMore || loading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    loadData(selectedPeriod, nextPage, true);
  };

  if (!isOpen) return null;

  const totalAmount = summary.totalNetAmount ?? summary.totalAmount ?? 0;

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(15, 15, 20, 0.55)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "16px",
    },
    modal: {
      width: "100%",
      maxWidth: "420px",
      maxHeight: "88vh",
      backgroundColor: "#f3f4f6",
      borderRadius: "20px",
      position: "relative",
      fontFamily: "'Nunito', 'Segoe UI', sans-serif",
      boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    headerBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px 16px 10px",
      position: "relative",
      flexShrink: 0,
    },
    closeBtn: {
      position: "absolute",
      right: "14px",
      top: "14px",
      width: "30px",
      height: "30px",
      borderRadius: "50%",
      backgroundColor: "#e5e7eb",
      border: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: "14px",
      color: "#374151",
    },
    titlePill: {
      backgroundColor: "#3b5bfd",
      color: "#fff",
      fontWeight: "800",
      fontSize: "16px",
      padding: "6px 16px",
      borderRadius: "8px",
    },
    scrollArea: {
      overflowY: "auto",
      padding: "4px 16px 20px",
    },
    subHeaderRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 2px 12px",
    },
    totalShifts: {
      fontWeight: "700",
      fontSize: "14px",
      color: "#111827",
    },
    selectWrapper: { position: "relative" },
    select: {
      appearance: "none",
      backgroundColor: "#fff",
      border: "1.5px solid #e5e7eb",
      borderRadius: "10px",
      padding: "7px 30px 7px 12px",
      fontSize: "13px",
      fontWeight: "600",
      color: "#111827",
      cursor: "pointer",
      outline: "none",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    },
    selectArrow: {
      position: "absolute",
      right: "10px",
      top: "50%",
      transform: "translateY(-50%)",
      pointerEvents: "none",
      fontSize: "11px",
      color: "#6b7280",
    },
    // ── Summary card — exact styling from PaymentHistory2 ──
    summaryCard: {
      background: "linear-gradient(135deg, #eef0ff 0%, #e8eaff 100%)",
      borderRadius: "18px",
      padding: "18px",
      position: "relative",
      overflow: "hidden",
      marginBottom: "16px",
    },
    watermark: {
      position: "absolute",
      right: "-10px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "150px",
      height: "150px",
      opacity: 0.8,
      pointerEvents: "none",
      userSelect: "none",
      filter: "brightness(0) invert(1)",
      objectFit: "contain",
    },
    summaryIconRow: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    summaryIcon: {
      width: "34px",
      height: "34px",
      borderRadius: "10px",
      backgroundColor: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 1px 4px rgba(99,102,241,0.15)",
    },
    summaryIconImg: { width: "18px", height: "18px", objectFit: "contain" },
    summaryLabel: {
      fontSize: "13px",
      color: "#5E5E5E",
      fontWeight: "600",
    },
    summaryAmount: {
      fontSize: "21px",
      fontWeight: "800",
      color: "#3C3C3C",
      marginBottom: "2px",
    },
    summaryAccount: {
      fontSize: "13px",
      color: "#222222",
      fontWeight: "500",
      margin: "12px 0 14px",
    },
    summarySubRow: { display: "flex", gap: "20px" },
    summarySubItem: { display: "flex", flexDirection: "column", gap: "3px" },
    summaryBorderBox: {
      width: "22px",
      height: "22px",
      borderRadius: "6px",
      backgroundColor: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    summarySubIcon: { width: "13px", height: "13px", objectFit: "contain" },
    summarySubLabel: { fontSize: "12px", color: "#303030", fontWeight: "500" },
    summarySubAmount: { fontSize: "15px", fontWeight: "700", color: "#3C3C3C" },
    // ── Payment list ──
    list: { display: "flex", flexDirection: "column", gap: "12px" },
    card: {
      backgroundColor: "#fff",
      borderRadius: "16px",
      padding: "14px",
      boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
    },
    statusRow: { display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" },
    statusDot: (color) => ({ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: color, flexShrink: 0 }),
    statusText: (color) => ({ fontSize: "12px", fontWeight: "700", color }),
    cardMainRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "4px" },
    companyName: { fontSize: "15px", fontWeight: "800", color: "#111827" },
    amount: (color) => ({ fontSize: "15px", fontWeight: "800", color }),
    txnRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" },
    txnId: { fontSize: "11px", color: "#9ca3af", fontWeight: "500" },
    paidOn: { fontSize: "11px", color: "#9ca3af", fontWeight: "500" },
    divider: { height: "1px", backgroundColor: "#f3f4f6", marginBottom: "10px" },
    dateTimeRow: { display: "flex", justifyContent: "space-between", gap: "12px" },
    dateTimeBlock: { flex: 1 },
    dateTimeLabel: { display: "flex", alignItems: "center", gap: "5px", fontSize: "10.5px", color: "#9ca3af", fontWeight: "500", marginBottom: "3px" },
    dateTimeValue: { fontSize: "12.5px", fontWeight: "700", color: "#111827" },
    calendarIcon: { width: "11px", height: "11px" },
    emptyState: { textAlign: "center", padding: "28px 16px", color: "#9ca3af", fontSize: "13px" },
    loadingText: { textAlign: "center", padding: "20px", color: "#9ca3af", fontSize: "13px" },
    viewAllLink: {
      textAlign: "center",
      padding: "10px 0 2px",
      fontSize: "13px",
      fontWeight: "700",
      color: "#3b5bfd",
      cursor: "pointer",
    },
    viewAllLinkDisabled: {
      textAlign: "center",
      padding: "10px 0 2px",
      fontSize: "13px",
      fontWeight: "700",
      color: "#9ca3af",
      cursor: "default",
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.headerBar}>
          <span className="text-xl font-bold">Payment History</span>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div style={styles.scrollArea}>
          <div style={styles.subHeaderRow}>
            <span style={styles.totalShifts}>Total Shifts : {summary.shiftCount ?? "-"}</span>
            <div style={styles.selectWrapper}>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                style={styles.select}
              >
                {generateMonthYearOptions().map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <span style={styles.selectArrow}>▼</span>
            </div>
          </div>

          <div style={styles.summaryCard}>
            <img src={rupeeIcon} alt="" style={styles.watermark} />
            <div style={styles.summaryIconRow}>
              <div style={styles.summaryIcon}>
                <img src={moneyCurrency2} alt="" style={styles.summaryIconImg} />
              </div>
              <div>
                <div style={styles.summaryLabel}>Total Amount Paid</div>
                <div style={styles.summaryAmount}>₹ {Number(totalAmount).toFixed(2)}</div>
              </div>
            </div>

            <div style={styles.summarySubRow}>
              <div style={styles.summarySubItem}>
                <div style={styles.summaryBorderBox}>
                  <img src={check} alt="" style={styles.summarySubIcon} />
                </div>
                <span style={styles.summarySubLabel}>Attendance Payout</span>
                <span style={styles.summarySubAmount}>
                  ₹ {Number(summary.attendancePayout ?? 0).toFixed(2)}
                </span>
              </div>
              <div style={styles.summarySubItem}>
                <div style={styles.summaryBorderBox}>
                  <img src={star} alt="" style={styles.summarySubIcon} />
                </div>
                <span style={styles.summarySubLabel}>Incentive Payout</span>
                <span style={styles.summarySubAmount}>
                  ₹ {Number(summary.incentivePayout ?? 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {!loading && payments.length === 0 ? (
            <div style={styles.emptyState}>
              {selectedPeriod === "all"
                ? "You haven't made any payments yet"
                : "No payments found for the selected period"}
            </div>
          ) : (
            <div style={styles.list}>
              {payments.map((payment, idx) => {
                const statusInfo = getStatus(payment.status || "");
                const isFailed = payment.status?.toLowerCase() === "failed";
                const isPending = payment.status?.toLowerCase() === "pending";
                const paidOnStr = formatPaidOn(payment.paidOn || payment.updatedAt);
                const amountColor = isFailed ? "#dc2626" : isPending ? "#d97706" : "#16a34a";
                const amount = payment.netAmount ?? payment.amount ?? 0;

                return (
                  <div key={payment.id || payment._id || idx} style={styles.card}>
                    <div style={styles.statusRow}>
                      <div style={styles.statusDot(statusInfo.color)} />
                      <span style={styles.statusText(statusInfo.color)}>{statusInfo.label}</span>
                    </div>

                    <div style={styles.cardMainRow}>
                      <span style={styles.companyName}>
                        {payment.companyName
                          ? `${payment.companyName.charAt(0).toUpperCase()}${payment.companyName.slice(1).toLowerCase()} (${payment.location || "-"})`
                          : "-"}
                      </span>
                      <span style={styles.amount(amountColor)}>
                        ₹ {Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>

                    <div style={styles.txnRow}>
                      <span style={styles.txnId}>
                        {payment.referenceId ? `Transaction ID : ${payment.referenceId}` : ""}
                      </span>
                      {paidOnStr && !isPending && (
                        <span style={styles.paidOn}>Paid on : {paidOnStr}</span>
                      )}
                    </div>

                    <div style={styles.divider} />

                    <div style={styles.dateTimeRow}>
                      <div style={styles.dateTimeBlock}>
                        <div style={styles.dateTimeLabel}>
                          <img src={calendar} alt="" style={styles.calendarIcon} /> Entry Date &amp; Time
                        </div>
                        <div style={styles.dateTimeValue}>{formatDateTime(payment.entryTime)}</div>
                      </div>
                      <div style={styles.dateTimeBlock}>
                        <div style={styles.dateTimeLabel}>
                          <img src={calendar} alt="" style={styles.calendarIcon} /> Exit Date &amp; Time
                        </div>
                        <div style={styles.dateTimeValue}>{formatDateTime(payment.exitTime)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {hasNextPage && (
                <div
                  style={loadingMore ? styles.viewAllLinkDisabled : styles.viewAllLink}
                  onClick={handleNextPage}
                >
                  {loadingMore ? "Loading..." : "Load More Entries →"}
                </div>
              )}
            </div>
          )}

          {loading && <div style={styles.loadingText}>Loading...</div>}
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailModal;